/*----------------------------------------------------------------------
    CLASS: ButtonLocker
----------------------------------------------------------------------*/
export class ButtonLocker {
    /*----------------------------------------------------------------------
        METHOD: constructor
        PARAMS: buttons = [], opts = { disableClass?: string = 'disabled' }
        NOTES:
            - Tracks elements we manage.
            - Uses a disable class for <a> tags and the native disabled prop for form controls.
    ----------------------------------------------------------------------*/
    constructor(buttons = [], opts = {}) {
        this._buttons = new Set();
        this._inCycle = false;
        this._initialDisabled = null;
        this._disabledByUs = null;
        this._anchorHandlers = new WeakMap();
        this._disableClass = opts.disableClass || 'disabled';
        this.add(buttons);
    }

    /*----------------------------------------------------------------------
        METHOD: add
        PARAMS: buttons = string|Element|Array<string|Element>|NodeList|Set<Element>
    ----------------------------------------------------------------------*/
    add(buttons) {
        for (const el of this._normalize(buttons)) this._buttons.add(el);
        return this;
    }

    /*----------------------------------------------------------------------
        METHOD: remove
        PARAMS: buttons = string|Element|Array<string|Element>|NodeList|Set<Element>
    ----------------------------------------------------------------------*/
    remove(buttons) {
        for (const el of this._normalize(buttons)) this._buttons.delete(el);
        return this;
    }

    /*----------------------------------------------------------------------
        METHOD: set
        PARAMS: buttons = string|Element|Array<string|Element>|NodeList|Set<Element>
        NOTES: Replaces the entire managed set.
    ----------------------------------------------------------------------*/
    set(buttons) {
        this._buttons.clear();
        return this.add(buttons);
    }

    /*----------------------------------------------------------------------
        METHOD: clear
        NOTES:
            - Clears managed elements and resets the cycle state.
            - Also clears any stored anchor handlers.
    ----------------------------------------------------------------------*/
    clear() {
        this._buttons.clear();
        this._inCycle = false;
        this._initialDisabled = null;
        this._disabledByUs = null;
        this._anchorHandlers = new WeakMap();
        return this;
    }

    /*----------------------------------------------------------------------
        METHOD: prune
        NOTES: Removes elements that are no longer connected to the DOM.
    ----------------------------------------------------------------------*/
    prune() {
        for (const btn of Array.from(this._buttons)) {
            if (!(btn instanceof Element) || !btn.isConnected) this._buttons.delete(btn);
        }
        return this;
    }

    /*----------------------------------------------------------------------
        METHOD: disableAll
        PARAMS: opts = { except?: string|Element|Array<string|Element>|NodeList|Set<Element> }
        RULES:
            - If an element has data-access="0", we CHECK its initial disabled state and remember it.
            - If it doesn't have data-access="0", we do NOT check; we simply disable it (unless in "except").
            - Any element in "except" is never disabled in this cycle.
            - <a> elements are disabled via class + aria-disabled + tabindex + click-capture.
            - Form controls use the native .disabled property.
    ----------------------------------------------------------------------*/
    disableAll(opts = {}) {
        if (this._inCycle) return this;

        const except = new Set(this._normalize(opts.except || []));
        this._initialDisabled = new WeakSet();
        this._disabledByUs = new WeakSet();
        this._inCycle = true;

        for (const el of this._buttons) {
            if (!(el instanceof Element)) continue;
            if (except.has(el)) continue;

            const requiresCheck = this._requiresCheck(el);
            const alreadyDisabled = this._isDisabled(el);

            if (requiresCheck && alreadyDisabled) {
                this._initialDisabled.add(el);
                continue;
            }

            if (!alreadyDisabled) {
                this._disableEl(el);
                this._disabledByUs.add(el);
            }
        }
        return this;
    }

    /*----------------------------------------------------------------------
        METHOD: enableAllowed
        RULES:
            - Only re-enable elements we actually disabled in this cycle.
            - Elements that started disabled (and those flagged via data-access="0") remain disabled.
            - For <a>, removes class/aria/tabindex and the click-capture handler.
    ----------------------------------------------------------------------*/
    enableAllowed() {
        if (!this._inCycle) return this;

        for (const el of this._buttons) {
            if (!(el instanceof Element)) continue;
            if (!this._disabledByUs || !this._disabledByUs.has(el)) continue;
            this._enableEl(el);
        }

        this._initialDisabled = null;
        this._disabledByUs = null;
        this._inCycle = false;
        return this;
    }

    /*----------------------------------------------------------------------
        METHOD: resetCycle
        NOTES: Clears cycle bookkeeping without changing current element states.
    ----------------------------------------------------------------------*/
    resetCycle() {
        this._initialDisabled = null;
        this._disabledByUs = null;
        this._inCycle = false;
        return this;
    }

    /*----------------------------------------------------------------------
        METHOD: _requiresCheck
        NOTES: Returns true if element has data-access="0".
    ----------------------------------------------------------------------*/
    _requiresCheck(el) {
        const v = el?.dataset?.access;
        return v === '0';
    }

    /*----------------------------------------------------------------------
        METHOD: _isAnchor
        NOTES: True if nodeName/tagName is 'A'.
    ----------------------------------------------------------------------*/
    _isAnchor(el) {
        return el && el.tagName === 'A';
    }

    /*----------------------------------------------------------------------
        METHOD: _isDisabled
        NOTES:
            - For <a>: checks disable class or aria-disabled="true".
            - For controls: checks .disabled boolean.
    ----------------------------------------------------------------------*/
    _isDisabled(el) {
        if (this._isAnchor(el)) {
            return el.classList.contains(this._disableClass) || el.getAttribute('aria-disabled') === 'true';
        }
        return !!el.disabled;
    }

    /*----------------------------------------------------------------------
        METHOD: _disableEl
        NOTES:
            - <a>: add class, aria-disabled, tabindex, and capture click to prevent navigation/handlers.
            - controls: set .disabled = true.
    ----------------------------------------------------------------------*/
    _disableEl(el) {
        if (this._isAnchor(el)) {
            el.classList.add(this._disableClass);
            el.setAttribute('aria-disabled', 'true');
            el.setAttribute('tabindex', '-1');
            if (!this._anchorHandlers.has(el)) {
                const handler = (e) => {
                    e.preventDefault();
                    e.stopImmediatePropagation();
                };
                el.addEventListener('click', handler, true);
                this._anchorHandlers.set(el, handler);
            }
        } else {
            el.disabled = true;
        }
    }

    /*----------------------------------------------------------------------
        METHOD: _enableEl
        NOTES:
            - <a>: remove class/aria/tabindex and the click-capture handler.
            - controls: set .disabled = false.
    ----------------------------------------------------------------------*/
    _enableEl(el) {
        if (this._isAnchor(el)) {
            el.classList.remove(this._disableClass);
            el.removeAttribute('aria-disabled');
            el.removeAttribute('tabindex');
            const handler = this._anchorHandlers.get(el);
            if (handler) {
                el.removeEventListener('click', handler, true);
                this._anchorHandlers.delete(el);
            }
        } else {
            el.disabled = false;
        }
    }

    /*----------------------------------------------------------------------
        METHOD: _normalize
        NOTES:
            - Accepts selector(s), Element(s), NodeList, Array, or Set.
            - Deduplicates by returning a flat array of Elements.
    ----------------------------------------------------------------------*/
    _normalize(input) {
        if (!input) return [];
        if (typeof input === 'string') return Array.from(document.querySelectorAll(input));
        if (input instanceof Element) return [input];
        if (input instanceof NodeList || Array.isArray(input) || input instanceof Set) {
            const out = [];
            for (const item of input) {
                if (typeof item === 'string') out.push(...document.querySelectorAll(item));
                else if (item instanceof Element) out.push(item);
            }
            return out;
        }
        return [];
    }
}

(function () {
    const block = (e) => {
        const el = e.target && e.target.closest('a.disabled');
        if (!el) return;
        e.preventDefault();
        e.stopImmediatePropagation();
    };

    window.addEventListener('pointerdown', block, true);
    window.addEventListener('click', block, true);
    window.addEventListener(
        'keydown',
        (e) => {
            if ((e.key === 'Enter' || e.key === ' ') && e.target.closest('a.disabled')) {
                e.preventDefault();
                e.stopImmediatePropagation();
            }
        },
        true
    );
})();
